import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { SECONDS_IN_A_DAY } from "@redotech/util/time";
import { XorOfUnions } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { CheckoutAbTestMetricToOptimize } from "../checkouts/checkout-ab-test-builder";
import {
  CheckoutProbabilitySegmentSchema,
  CustomerReturnRateSegmentSchema,
  CustomerTagsSegmentSchema,
  DatetimeRangeSegmentSchema,
  DeliveryDistanceMilesSegmentSchema,
  DeliveryLocationSegmentSchema,
  ItemQuantitySegmentSchema,
  LineItemPropertiesSegmentSchema,
  OrderCountSegmentSchema,
  OriginLocationSegmentSchema,
  ProductCollectionsSegmentSchema,
  ProductSkuSegmentSchema,
  ProductTagsSegmentSchema,
  ProductVendorSegmentSchema,
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
  TimeOfDaySegmentSchema,
} from "../checkouts/checkout-tree";
import { ParcelConditionKind } from "../checkouts/parcel/parcel-tree";
import { CheckoutOptimizationWidgetType } from "../checkouts/widget";
import {
  Carriers,
  CarrierSchema,
  ServiceSchema,
} from "../fulfillments/fulfillment-carriers-and-services";
import { RedoShopifyDeliveryMethodType } from "../fulfillments/fulfillment-delivery-method-type";
import { SortingCriteria } from "../fulfillments/fulfillment-group";
import { FulfillmentOrderAddressWithIdSchema } from "../fulfillments/fulfillment-order-address";
import { Hazmat } from "../fulfillments/hazmat";
import { ShopifyFulfillmentHoldReason } from "../fulfillments/shopify-fulfillment-schemas";
import {
  afterNowRelativeExactTimeframeSchema,
  beforeNowRelativeExactTimeframeSchema,
  todayTimeframeSchema,
} from "../marketing/segments/segment-timeframe-zod-schema";
import { CustomerCharacteristicType } from "../marketing/segments/segment-types";
import { SpecialRateEligibility } from "../outbound-labels/outbound-labels";
import { OutboundParcelSchema, ParcelSchema } from "../outbound-labels/parcel";
import { WeightSchema } from "../outbound-labels/util";
import { SegmentablePostPurchaseCharacteristic } from "../post-purchase/post-purchase-flow";
import { PostPurchaseSegmentNumberOperator } from "../post-purchase/segment-types";
import { ConjunctionMode } from "../segments/segment-types";
import { checkoutOptimizationTreatmentStorefrontFeaturesSchema } from "../split";
import { TagSchema } from "../tag";
import { TimeUnit } from "../time";
import { supportedWaitForEventTypes } from "./common";
import { ListToListMatchType, StringToListMatchType } from "./common/condition";
import { dateComparisonOperators } from "./expressions/date-expression";
import {
  numberComparisonInequalityOperators,
  numberComparisonOperators,
} from "./expressions/number-expression";
import { textMatchOperators } from "./expressions/text-expression";
import { YotpoIntegrationTriggerKey } from "./integration-triggers";
import {
  AutoHoldType,
  OmsActionType,
  OmsSettableFields,
  RoundWeightActionStep,
  SetWeightActionStep,
  WeightRoundingType,
} from "./oms/actions";
import { OmsConditionType } from "./oms/conditions";
import {
  ruleActionsStepSchema,
  ruleConditionsStepSchema,
} from "./rules/schemas";
import { RuleTriggerType } from "./rules/types";
import { SchemaType } from "./schemas/schemas";
import {
  ConditionDataSource,
  StepType,
  UpsellConditionSource,
  WaitTimeUnit,
} from "./steps";
import {
  categories,
  Category,
  CheckoutOptimizationTriggerKey,
  CheckoutParcelTriggerKey,
  CustomEventTriggerKey,
  EwTriggerKey,
  MarketingTriggerKey,
  OmsTriggerKey,
  OrderTrackingTriggerKey,
  PostPurchaseTriggerKey,
  ProductSubscriptionTriggerKey,
  ReturnTrackingTriggerKey,
  ReviewsTriggerKey,
  TnplTriggerKey,
  TrackingPageTriggerKey,
  TriggerKey,
} from "./triggers";

export const ORIGIN_RETURN_TRACKING_TEAM_ID = "68f7e6321b01371d0025d1c5";

export function waitStepSeconds(step: Partial<WaitStep>): number | undefined {
  if (step.numSeconds !== undefined) {
    return step.numSeconds;
  } else if (step.numDays !== undefined) {
    return step.numDays * SECONDS_IN_A_DAY;
  }
  return undefined;
}

const baseStepSchema = z.object({
  customTitle: z.string().optional(),
  type: createEnum(StepType),
  id: z.string(),
});

const baseOmsActionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.OMS_ACTION),
  actionType: createEnum(OmsActionType),
  field: createEnum(OmsSettableFields),
  nextId: z.string(),
});

// Probably should just convert categories to an enum
const orderTrackingCategory: (typeof categories)[number] = "Order tracking";
const returnTrackingCategory: (typeof categories)[number] = "Return tracking";
const tnplCategory: (typeof categories)[number] = "Try first";
const marketingCategory: (typeof categories)[number] = "Marketing";
const reviewsCategory: (typeof categories)[number] = "Reviews";
const omsCategory: (typeof categories)[number] = "Oms";
const checkoutOptimizationCategory: (typeof categories)[number] =
  "Checkout personalization";
const checkoutPackageCategory: (typeof categories)[number] = "Checkout package";
const upsellCategory: (typeof categories)[number] = "Upsell";
const postPurchaseCategory: (typeof categories)[number] = "Post-purchase";
const trackingPageCategory: (typeof categories)[number] = "Tracking page";
const ewCategory: (typeof categories)[number] = "Extended warranty";
const integrationCategory: (typeof categories)[number] = "Integration";
const customEventCategory: (typeof categories)[number] = "Custom Event";
const rulesCategory: (typeof categories)[number] = "Rules";

export const schemaBooleanExpressionSchema: z.ZodType<SchemaBooleanExpressionType> =
  z.lazy(() =>
    z.union([
      numberComparisonExpressionSchema,
      textMatchExpressionSchema,
      arrayPredicateExpressionSchema,
      booleanEvaluationExpressionSchema,
    ]),
  );

export const actionStepTypes = [
  StepType.SEND_EMAIL,
  StepType.SEND_SMS,
  StepType.SEND_WEBHOOK,
  StepType.MANAGE_CUSTOMER_TAGS,
  StepType.RECOVER,
] as const satisfies StepType[];
export type ActionStepType = (typeof actionStepTypes)[number];

export const actionStepTypeToFriendlyName: Record<ActionStepType, string> = {
  [StepType.SEND_EMAIL]: "Send email",
  [StepType.SEND_SMS]: "Send SMS",
  [StepType.SEND_WEBHOOK]: "Send webhook",
  [StepType.MANAGE_CUSTOMER_TAGS]: "Manage customer tags",
  [StepType.RECOVER]: "Recover",
};

const conditionExpressionSchema = z.intersection(
  z.object({ _id: zExt.objectId().optional() }),
  z.union([
    z.object({
      dataSource: z.literal(ConditionDataSource.TriggerData),
      schemaBooleanExpression: schemaBooleanExpressionSchema,
    }),
    z.object({
      dataSource: z.literal(ConditionDataSource.ExistingSegment),
      segmentIds: z.array(z.string()),
    }),
    z.object({
      dataSource: z.literal(ConditionDataSource.InlineSegment),
      inlineSegment: z.object({
        mode: createEnum(ConjunctionMode),
        conditions: z.array(z.any()), //TODO: Replace with queryConditionSchema (requires migration)
      }),
    }),
    z.object({ dataSource: z.literal(ConditionDataSource.OfferResponse) }),
  ]),
);
export type ConditionExpression = z.infer<typeof conditionExpressionSchema>;

const skipConditionSchema = z.intersection(
  conditionExpressionSchema,
  z.object({ actionTypes: z.array(createEnum(actionStepTypes)).nullish() }),
);
export type SkipCondition = z.infer<typeof skipConditionSchema>;

const baseTriggerStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.TRIGGER),
  schemaType: createEnum(SchemaType),
  category: createEnum(categories),
  nextId: z.string(),
  skipConditions: z
    .object({
      conjunctionMode: z.literal(ConjunctionMode.OR), // Currently only OR is supported, but I figure we may as well include the field for future proofing.
      conditions: z.array(skipConditionSchema),
    })
    .optional(),
});

export const flowCategoriesWithSkipConditions: Category[] = [
  "Marketing",
  "Order tracking",
];

// Define specific trigger step schemas for each trigger type
const orderTrackingTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(orderTrackingCategory),
  key: createEnum(OrderTrackingTriggerKey),
});
const orderTrackingTriggerStepSchema: z.ZodType<OrderTrackingTriggerStep> =
  orderTrackingTriggerStepSchema_;
export interface OrderTrackingTriggerStep
  extends z.infer<typeof orderTrackingTriggerStepSchema_> {}

const returnTrackingTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(returnTrackingCategory),
  key: createEnum(ReturnTrackingTriggerKey),
});
const returnTrackingTriggerStepSchema: z.ZodType<ReturnTrackingTriggerStep> =
  returnTrackingTriggerStepSchema_;
export interface ReturnTrackingTriggerStep
  extends z.infer<typeof returnTrackingTriggerStepSchema_> {}

const tnplTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(tnplCategory),
  key: createEnum(TnplTriggerKey),
});
const tnplTriggerStepSchema: z.ZodType<TnplTriggerStep> =
  tnplTriggerStepSchema_;
export interface TnplTriggerStep
  extends z.infer<typeof tnplTriggerStepSchema_> {}

const baseMarketingTriggerStepSchema = baseTriggerStepSchema.extend({
  category: z.literal(marketingCategory),
});
// Break down marketing triggers into individual schemas
const emailSignupTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.EMAIL_SIGNUP),
});
const emailSignupTriggerStepSchema: z.ZodType<EmailSignupTriggerStep> =
  emailSignupTriggerStepSchema_;
export interface EmailSignupTriggerStep
  extends z.infer<typeof emailSignupTriggerStepSchema_> {}

export const emailSignupShopifyTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY),
  });
const emailSignupShopifyTriggerStepSchema: z.ZodType<EmailSignupShopifyTriggerStep> =
  emailSignupShopifyTriggerStepSchema_;
export interface EmailSignupShopifyTriggerStep
  extends z.infer<typeof emailSignupShopifyTriggerStepSchema_> {}

const smsSignupTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.SMS_SIGNUP),
});
// Backwards compatibility: normalize deprecated "sms_signup" key to "sms_confirmed"
const smsSignupTriggerStepSchema: z.ZodType<SmsSignupTriggerStep> =
  z.preprocess((val: any) => {
    if (!val) return val;
    // Convert Mongoose document to plain object if needed
    const plainObj = val.toObject ? val.toObject() : val;
    if (plainObj.key === "sms_signup") {
      plainObj.key = "sms_confirmed";
    }
    return plainObj;
  }, smsSignupTriggerStepSchema_) as z.ZodType<SmsSignupTriggerStep>;
// TODO: Switch to cleaner version in mid-December 2025 after outdated flows in temporal have all finished
// Replace above with:
// const smsSignupTriggerStepSchema: z.ZodType<SmsSignupTriggerStep> =
//   smsSignupTriggerStepSchema_;
export interface SmsSignupTriggerStep
  extends z.infer<typeof smsSignupTriggerStepSchema_> {}

const marketingCampaignTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.MARKETING_CAMPAIGN),
  });
const marketingCampaignTriggerStepSchema: z.ZodType<MarketingCampaignTriggerStep> =
  marketingCampaignTriggerStepSchema_;
export interface MarketingCampaignTriggerStep
  extends z.infer<typeof marketingCampaignTriggerStepSchema_> {}

const cartAbandonedTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.CART_ABANDONED),
});
const cartAbandonedTriggerStepSchema: z.ZodType<CartAbandonedTriggerStep> =
  cartAbandonedTriggerStepSchema_;
export interface CartAbandonedTriggerStep
  extends z.infer<typeof cartAbandonedTriggerStepSchema_> {}

const browseAbandonedTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend(
  { key: z.literal(MarketingTriggerKey.BROWSE_ABANDONED) },
);
const browseAbandonedTriggerStepSchema: z.ZodType<BrowseAbandonedTriggerStep> =
  browseAbandonedTriggerStepSchema_;
export interface BrowseAbandonedTriggerStep
  extends z.infer<typeof browseAbandonedTriggerStepSchema_> {}

export const checkoutAbandonedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CHECKOUT_ABANDONED),
  });
const checkoutAbandonedTriggerStepSchema: z.ZodType<CheckoutAbandonedTriggerStep> =
  checkoutAbandonedTriggerStepSchema_;
export interface CheckoutAbandonedTriggerStep
  extends z.infer<typeof checkoutAbandonedTriggerStepSchema_> {}

const csCartAbandonedTriggerStepSchema_ = baseMarketingTriggerStepSchema.extend(
  { key: z.literal(MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED) },
);
const csCartAbandonedTriggerStepSchema: z.ZodType<CsCartAbandonedTriggerStep> =
  csCartAbandonedTriggerStepSchema_;
export interface CsCartAbandonedTriggerStep
  extends z.infer<typeof csCartAbandonedTriggerStepSchema_> {}

const csBrowseAbandonedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED),
  });
const csBrowseAbandonedTriggerStepSchema: z.ZodType<CsBrowseAbandonedTriggerStep> =
  csBrowseAbandonedTriggerStepSchema_;
export interface CsBrowseAbandonedTriggerStep
  extends z.infer<typeof csBrowseAbandonedTriggerStepSchema_> {}

const csCheckoutAbandonedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED),
  });
const csCheckoutAbandonedTriggerStepSchema: z.ZodType<CsCheckoutAbandonedTriggerStep> =
  csCheckoutAbandonedTriggerStepSchema_;
export interface CsCheckoutAbandonedTriggerStep
  extends z.infer<typeof csCheckoutAbandonedTriggerStepSchema_> {}

export const backInStockTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.BACK_IN_STOCK),
  });

export const customerGroupEnteredTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CUSTOMER_GROUP_ENTERED),
  });
const customerGroupEnteredTriggerStepSchema: z.ZodType<CustomerGroupEnteredTriggerStep> =
  customerGroupEnteredTriggerStepSchema_;
export interface CustomerGroupEnteredTriggerStep
  extends z.infer<typeof customerGroupEnteredTriggerStepSchema_> {}

export const customerGroupExitedTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CUSTOMER_GROUP_EXITED),
  });
const customerGroupExitedTriggerStepSchema: z.ZodType<CustomerGroupExitedTriggerStep> =
  customerGroupExitedTriggerStepSchema_;
export interface CustomerGroupExitedTriggerStep
  extends z.infer<typeof customerGroupExitedTriggerStepSchema_> {}

export const warrantyRegistrationTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.WARRANTY_REGISTRATION),
  });

const marketingLowInventoryTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.LOW_INVENTORY),
  });
const marketingLowInventoryTriggerStepSchema: z.ZodType<MarketingLowInventoryTriggerStep> =
  marketingLowInventoryTriggerStepSchema_;
export interface MarketingLowInventoryTriggerStep
  extends z.infer<typeof marketingLowInventoryTriggerStepSchema_> {}

const marketingDateTriggerDimensions = [
  CustomerCharacteristicType.BIRTHDAY,
] as const;
export type MarketingDateTriggerDimension =
  (typeof marketingDateTriggerDimensions)[number];

const marketingDateTriggerStepSchema = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.DATE),
  triggerSpecificFields: z.object({
    dimension: createEnum(CustomerCharacteristicType),
    comparison: z.union([
      beforeNowRelativeExactTimeframeSchema,
      afterNowRelativeExactTimeframeSchema,
      todayTimeframeSchema,
    ]),
  }),
});
export type MarketingDateTriggerStep = z.infer<
  typeof marketingDateTriggerStepSchema
>;

export enum PriceDropAmountType {
  PERCENTAGE = "percentage",
  CURRENCY = "currency",
}
const priceDropAmountTypeSchema = createEnum(PriceDropAmountType);

const priceDropContactRequirementsSchema = z.object({
  productViewed: z.boolean(),
  addedToCart: z.boolean(),
  startedCheckout: z.boolean(),
});
export type PriceDropContactRequirements = z.infer<
  typeof priceDropContactRequirementsSchema
>;

const marketingPriceDropTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.PRICE_DROP),
    triggerSpecificFields: z.object({
      minimumPriceDropAmount: z.object({
        type: priceDropAmountTypeSchema,
        value: z.number().positive(),
      }),
      contactRequirements: priceDropContactRequirementsSchema,
      daysBack: z.number().int().min(1).max(365).default(30),
    }),
  });
export type MarketingPriceDropTriggerStep = z.infer<
  typeof marketingPriceDropTriggerStepSchema
>;

const ProductSubscriptionTriggerStepSchema_ =
  baseMarketingTriggerStepSchema.extend({
    key: createEnum(ProductSubscriptionTriggerKey),
  });
const ProductSubscriptionTriggerStepSchema: z.ZodType<ProductSubscriptionTriggerStep> =
  ProductSubscriptionTriggerStepSchema_;
export interface ProductSubscriptionTriggerStep
  extends z.infer<typeof ProductSubscriptionTriggerStepSchema_> {}

export const marketingTriggerStepSchemas = z.union([
  emailSignupTriggerStepSchema,
  emailSignupShopifyTriggerStepSchema,
  smsSignupTriggerStepSchema,
  marketingCampaignTriggerStepSchema,
  cartAbandonedTriggerStepSchema,
  browseAbandonedTriggerStepSchema,
  checkoutAbandonedTriggerStepSchema,
  csCartAbandonedTriggerStepSchema,
  csBrowseAbandonedTriggerStepSchema,
  csCheckoutAbandonedTriggerStepSchema,
  backInStockTriggerStepSchema,
  customerGroupEnteredTriggerStepSchema,
  customerGroupExitedTriggerStepSchema,
  warrantyRegistrationTriggerStepSchema,
  marketingLowInventoryTriggerStepSchema,
  marketingDateTriggerStepSchema,
  marketingPriceDropTriggerStepSchema,
  ProductSubscriptionTriggerStepSchema,
]);

const reviewsTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(reviewsCategory),
  key: createEnum(ReviewsTriggerKey),
});
const reviewsTriggerStepSchema: z.ZodType<ReviewsTriggerStep> =
  reviewsTriggerStepSchema_;
export interface ReviewsTriggerStep
  extends z.infer<typeof reviewsTriggerStepSchema_> {}

const omsTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(omsCategory),
  key: createEnum(OmsTriggerKey),
});
const omsTriggerStepSchema: z.ZodType<OmsTriggerStep> = omsTriggerStepSchema_;
export interface OmsTriggerStep extends z.infer<typeof omsTriggerStepSchema_> {}

const checkoutOptimizationTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(checkoutOptimizationCategory),
  key: createEnum(CheckoutOptimizationTriggerKey),
});
const checkoutOptimizationTriggerStepSchema: z.ZodType<CheckoutOptimizationTriggerStep> =
  checkoutOptimizationTriggerStepSchema_;
export interface CheckoutOptimizationTriggerStep
  extends z.infer<typeof checkoutOptimizationTriggerStepSchema_> {}

const checkoutPackageTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(checkoutPackageCategory),
  key: createEnum(CheckoutParcelTriggerKey),
});
const checkoutPackageTriggerStepSchema: z.ZodType<CheckoutPackageTriggerStep> =
  checkoutPackageTriggerStepSchema_;
export interface CheckoutPackageTriggerStep
  extends z.infer<typeof checkoutPackageTriggerStepSchema_> {}

const upsellTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(upsellCategory),
  key: z.literal(OrderTrackingTriggerKey.POST_PURCHASE_UPSELL),
});
const upsellTriggerStepSchema: z.ZodType<UpsellTriggerStep> =
  upsellTriggerStepSchema_;
export interface UpsellTriggerStep
  extends z.infer<typeof upsellTriggerStepSchema_> {}

const postPurchaseTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(postPurchaseCategory),
  key: createEnum(PostPurchaseTriggerKey),
});
const postPurchaseTriggerStepSchema: z.ZodType<PostPurchaseTriggerStep> =
  postPurchaseTriggerStepSchema_;
export interface PostPurchaseTriggerStep
  extends z.infer<typeof postPurchaseTriggerStepSchema_> {}

const trackingPageTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(trackingPageCategory),
  key: createEnum(TrackingPageTriggerKey),
});
const trackingPageTriggerStepSchema: z.ZodType<TrackingPageTriggerStep> =
  trackingPageTriggerStepSchema_;
export interface TrackingPageTriggerStep
  extends z.infer<typeof trackingPageTriggerStepSchema_> {}

const extendedWarrantyTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(ewCategory),
  key: createEnum(EwTriggerKey),
});
const extendedWarrantyTriggerStepSchema: z.ZodType<ExtendedWarrantyTriggerStep> =
  extendedWarrantyTriggerStepSchema_;
export interface ExtendedWarrantyTriggerStep
  extends z.infer<typeof extendedWarrantyTriggerStepSchema_> {}

const yotpoTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(integrationCategory),
  key: createEnum(YotpoIntegrationTriggerKey),
});
const yotpoTriggerStepSchema: z.ZodType<YotpoIntegrationTriggerStep> =
  yotpoTriggerStepSchema_;
export interface YotpoIntegrationTriggerStep
  extends z.infer<typeof yotpoTriggerStepSchema_> {}

const customEventTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(customEventCategory),
  key: z.literal(CustomEventTriggerKey.CUSTOM_EVENT),
  eventName: z.string(),
});
const customEventTriggerStepSchema: z.ZodType<CustomEventTriggerStep> =
  customEventTriggerStepSchema_;
export interface CustomEventTriggerStep
  extends z.infer<typeof customEventTriggerStepSchema_> {}

const rulesTriggerStepSchema_ = baseTriggerStepSchema.extend({
  category: z.literal(rulesCategory),
  key: createEnum(RuleTriggerType),
});
const rulesTriggerStepSchema: z.ZodType<RulesTriggerStep> =
  rulesTriggerStepSchema_;
export interface RulesTriggerStep
  extends z.infer<typeof rulesTriggerStepSchema_> {}

// TODO: Once zod 4 is released, switch to discriminated union
export const triggerStepSchema = z.union([
  orderTrackingTriggerStepSchema,
  returnTrackingTriggerStepSchema,
  tnplTriggerStepSchema,
  marketingTriggerStepSchemas,
  reviewsTriggerStepSchema,
  omsTriggerStepSchema,
  checkoutOptimizationTriggerStepSchema,
  checkoutPackageTriggerStepSchema,
  upsellTriggerStepSchema,
  postPurchaseTriggerStepSchema,
  trackingPageTriggerStepSchema,
  extendedWarrantyTriggerStepSchema,
  yotpoTriggerStepSchema,
  customEventTriggerStepSchema,
  rulesTriggerStepSchema,
]);
export type TriggerStep = z.infer<typeof triggerStepSchema>;

const waitStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.WAIT),
  numDays: z.number(),
  numSeconds: z.number().optional(),
  timeUnit: createEnum(WaitTimeUnit).default(WaitTimeUnit.DAYS).optional(),
  nextId: z.string(),
});
const waitStepSchema: z.ZodType<WaitStep> = waitStepSchema_;
export interface WaitStep extends z.infer<typeof waitStepSchema_> {}

const waitForEventStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.WAIT_FOR_EVENT),
  eventType: createEnum(supportedWaitForEventTypes),
  nextId: z.string(),
});
const waitForEventStepSchema: z.ZodType<WaitForEventStep> =
  waitForEventStepSchema_;
export interface WaitForEventStep
  extends z.infer<typeof waitForEventStepSchema_> {}

const sendEmailStepBaseSchema = baseStepSchema.extend({
  type: z.literal(StepType.SEND_EMAIL),
  emailAddressFieldName: z.string(),
  recipientNameFieldName: z.string(),
  nextId: z.string().optional(),
  disabled: z.boolean().optional(),
});

const sendEmailStepSchema_ = z.intersection(
  sendEmailStepBaseSchema,
  z.union([
    z.object({ templateId: z.string(), originTemplateId: z.undefined() }),
    z.object({ templateId: z.undefined(), originTemplateId: z.string() }),
  ]),
);

export const sendEmailStepSchema: z.ZodType<SendEmailStep> =
  sendEmailStepSchema_;
interface SendEmailTemplate extends z.infer<typeof sendEmailStepBaseSchema> {
  templateId: string;
  originTemplateId?: never;
}
interface SendEmailOriginTemplate
  extends z.infer<typeof sendEmailStepBaseSchema> {
  originTemplateId: string;
  templateId?: never;
}

export type SendEmailStep = SendEmailTemplate | SendEmailOriginTemplate;

const sendSmsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.SEND_SMS),
  // templateId can be either an ObjectId string or a special template identifier (e.g., "special-text-y-to-confirm-subscription")
  templateId: z.string(),
  phoneNumberFieldName: z.string(),
  recipientNameFieldName: z.string(),
  nextId: z.string().optional(),
  disabled: z.boolean().optional(),
});

export const sendSmsStepSchema: z.ZodType<SendSmsStep> = sendSmsStepSchema_;
export type SendSmsStep = z.infer<typeof sendSmsStepSchema_>;

export enum SendWebhookStepAuthType {
  NONE = "none",
  OAUTH2_CLIENT_CREDENTIALS = "oauth2_client_credentials",
}

const sendWebhookStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.SEND_WEBHOOK),
  destinationUrl: z.string(),
  headers: z.array(z.object({ key: z.string(), value: z.string() })),
  payload: z.string(),
  nextId: z.string().nullish(),
  disabled: z.boolean(),
  authType: createEnum(SendWebhookStepAuthType).nullish(),
  oauth2Config: z
    .object({
      tokenUrl: z.string(),
      clientId: z.string(),
      clientSecret: z.string(),
      audience: z.string().nullish(),
      scope: z.string().nullish(),
    })
    .nullish(),
});
const sendWebhookStepSchema: z.ZodType<SendWebhookStep> =
  sendWebhookStepSchema_;
export interface SendWebhookStep
  extends z.infer<typeof sendWebhookStepSchema_> {}

export type ActionStep =
  | SendEmailStep
  | SendSmsStep
  | SendWebhookStep
  | ManageCustomerTagsStep
  | RecoverStep;
export function isActionStep(step: FlowStep): step is ActionStep {
  return actionStepTypes.includes(step.type as ActionStep["type"]);
}

// Customer Tag Management Step
export enum CustomerTagOperation {
  ADD = "add",
  REMOVE = "remove",
}

const manageCustomerTagsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.MANAGE_CUSTOMER_TAGS),
  operation: createEnum(CustomerTagOperation),
  tagIds: z.array(z.string()).min(1),
  nextId: z.string(),
  disabled: z.boolean(),
});
const manageCustomerTagsStepSchema: z.ZodType<ManageCustomerTagsStep> =
  manageCustomerTagsStepSchema_;
export interface ManageCustomerTagsStep
  extends z.infer<typeof manageCustomerTagsStepSchema_> {}

const recoverStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RECOVER),
  templateId: z.string(),
  phoneNumberFieldName: z.string(),
  nextId: z.string(),
  disabled: z.boolean(),
});
const recoverStepSchema: z.ZodType<RecoverStep> = recoverStepSchema_;
export interface RecoverStep extends z.infer<typeof recoverStepSchema_> {}

const numberComparisonExpressionSchema_ = z.object({
  type: z.literal("number_comparison"),
  field: z.union([z.string(), z.number()]),
  operator: createEnum(numberComparisonOperators),
  comparisonValue: z.number(),
});
const numberComparisonExpressionSchema: z.ZodType<NumberComparisonExpression> =
  numberComparisonExpressionSchema_;
export interface NumberComparisonExpression
  extends z.infer<typeof numberComparisonExpressionSchema_> {}

const textMatchExpressionSchema_ = z.object({
  type: z.literal("text_match"),
  field: z.union([z.string(), z.number()]),
  operator: createEnum(["startsWith", "endsWith", "includes", "equals"]),
  matchValues: z.array(z.string()),
});
const textMatchExpressionSchema: z.ZodType<TextMatchExpression> =
  textMatchExpressionSchema_;
export interface TextMatchExpression
  extends z.infer<typeof textMatchExpressionSchema_> {}

export type SchemaBooleanExpressionType =
  | NumberComparisonExpression
  | TextMatchExpression
  | ArrayPredicateExpression
  | BooleanEvaluationExpression;

const arrayPredicateExpressionSchema_ = z.object({
  type: z.literal("array_predicate"),
  field: z.union([z.string(), z.number()]),
  operator: createEnum(["some", "every"]),
  get condition() {
    return schemaBooleanExpressionSchema;
  },
});
const arrayPredicateExpressionSchema: z.ZodType<ArrayPredicateExpression> =
  arrayPredicateExpressionSchema_;
export interface ArrayPredicateExpression
  extends z.infer<typeof arrayPredicateExpressionSchema_> {}

const booleanEvaluationExpressionSchema_ = z.object({
  type: z.literal("boolean_evaluation"),
  field: z.union([z.string(), z.number()]),
  value: z.boolean().optional(),
});
const booleanEvaluationExpressionSchema: z.ZodType<BooleanEvaluationExpression> =
  booleanEvaluationExpressionSchema_;
export interface BooleanEvaluationExpression
  extends z.infer<typeof booleanEvaluationExpressionSchema_> {}

const conditionStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.CONDITION),
  expression: conditionExpressionSchema,
  nextTrueId: z.string(),
  nextFalseId: z.string(),
});
export const conditionStepSchema: z.ZodType<ConditionStep> =
  conditionStepSchema_;
export interface ConditionStep extends z.infer<typeof conditionStepSchema_> {}

const upsellConditionStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.UPSELL_CONDITION),
  expression: z
    .object({
      source: createEnum(UpsellConditionSource),
      products: z
        .array(z.object({ id: z.string(), name: z.string() }))
        .optional(),
      collection: z.string().optional(),
    })
    .optional(),
  isDefault: z.boolean().optional(),
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
  name: z.string().optional(),
});
const upsellConditionStepSchema: z.ZodType<UpsellConditionStep> =
  upsellConditionStepSchema_;
export interface UpsellConditionStep
  extends z.infer<typeof upsellConditionStepSchema_> {}

const upsellStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.UPSELL),
  name: z.string().optional(),
  upsellId: z.string(),
  nextId: z.string().optional(),
});
const upsellStepSchema: z.ZodType<UpsellStep> = upsellStepSchema_;
export interface UpsellStep extends z.infer<typeof upsellStepSchema_> {}

const downsellStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.DOWNSELL),
  name: z.string().optional(),
  downsellId: z.string(),
  nextId: z.string().optional(),
});
const downsellStepSchema: z.ZodType<DownsellStep> = downsellStepSchema_;
export interface DownsellStep extends z.infer<typeof downsellStepSchema_> {}

const doNothingStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.DO_NOTHING),
});
const doNothingStepSchema: z.ZodType<DoNothingStep> = doNothingStepSchema_;
export interface DoNothingStep extends z.infer<typeof doNothingStepSchema_> {}

/* Checkout personalization */
const rcoSplitStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RCO_SPLIT),
  name: z.string(),
  hypothesis: z.string(),
  splitId: zExt.objectId(),
  metricToOptimize: createEnum(CheckoutAbTestMetricToOptimize),
  treatments: z.array(
    z.object({
      treatmentId: zExt.objectId(),
      nextId: z.string(),
      name: z.string(),
      weight: z.number(),
      storefrontFeatures:
        checkoutOptimizationTreatmentStorefrontFeaturesSchema.optional(),
    }),
  ),
  isActive: z.boolean(),
});
const rcoSplitStepSchema: z.ZodType<RcoSplitStep> = rcoSplitStepSchema_;
export interface RcoSplitStep extends z.infer<typeof rcoSplitStepSchema_> {}

const checkoutExperienceStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.CHECKOUT_EXPERIENCE),
  name: z.string(),
  widgets: z.array(createEnum(CheckoutOptimizationWidgetType)),
  checkoutExperienceId: zExt.objectId(),
  nextId: z.string().optional(), // Should never be set
});
const checkoutExperienceStepSchema: z.ZodType<CheckoutExperienceStep> =
  checkoutExperienceStepSchema_;
export type CheckoutExperienceStep = z.infer<
  typeof checkoutExperienceStepSchema_
>;

const blockCheckoutStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.BLOCK_CHECKOUT),
  nextId: z.string().optional(), // Should never be set
});
const blockCheckoutStepSchema: z.ZodType<BlockCheckoutStep> =
  blockCheckoutStepSchema_;
export type BlockCheckoutStep = z.infer<typeof blockCheckoutStepSchema_>;

/* Segmentation */
export const rcoDeliveryDistanceMilesSegmentSchema =
  DeliveryDistanceMilesSegmentSchema;
export type RcoDeliveryDistanceMilesSegmentSchema = z.infer<
  typeof rcoDeliveryDistanceMilesSegmentSchema
>;
export const rcoOrderCountSegmentSchema = OrderCountSegmentSchema;
export type RcoOrderCountSegmentSchema = z.infer<
  typeof rcoOrderCountSegmentSchema
>;
export const rcoDeliveryLocationSegmentSchema = DeliveryLocationSegmentSchema;
export type RcoDeliveryLocationSegmentSchema = z.infer<
  typeof rcoDeliveryLocationSegmentSchema
>;
export const rcoCustomerTagsSegmentSchema = CustomerTagsSegmentSchema;
export type RcoCustomerTagsSegmentSchema = z.infer<
  typeof rcoCustomerTagsSegmentSchema
>;
export const rcoProductTagsSegmentSchema = ProductTagsSegmentSchema;
export type RcoProductTagsSegmentSchema = z.infer<
  typeof rcoProductTagsSegmentSchema
>;
export const rcoProductCollectionsSegmentSchema =
  ProductCollectionsSegmentSchema;
export type RcoProductCollectionsSegmentSchema = z.infer<
  typeof rcoProductCollectionsSegmentSchema
>;
export const rcoProductVendorSegmentSchema = ProductVendorSegmentSchema;
export type RcoProductVendorSegmentSchema = z.infer<
  typeof rcoProductVendorSegmentSchema
>;
export const rcoProductSkuSegmentSchema = ProductSkuSegmentSchema;
export type RcoProductSkuSegmentSchema = z.infer<
  typeof rcoProductSkuSegmentSchema
>;
export const rcoItemQuantitySegmentSchema = ItemQuantitySegmentSchema;
export type RcoItemQuantitySegmentSchema = z.infer<
  typeof rcoItemQuantitySegmentSchema
>;
export const rcoOriginLocationSegmentSchema = OriginLocationSegmentSchema;
export type RcoOriginLocationSegmentSchema = z.infer<
  typeof rcoOriginLocationSegmentSchema
>;

export const rcoCustomerReturnRateSegmentSchema =
  CustomerReturnRateSegmentSchema;
export type RcoCustomerReturnRateSegmentSchema = z.infer<
  typeof rcoCustomerReturnRateSegmentSchema
>;

export const rcoTimeOfDaySegmentSchema = TimeOfDaySegmentSchema;
export type RcoTimeOfDaySegmentSchema = z.infer<
  typeof rcoTimeOfDaySegmentSchema
>;

export const rcoDatetimeRangeSegmentSchema = DatetimeRangeSegmentSchema;
export type RcoDatetimeRangeSegmentSchema = z.infer<
  typeof rcoDatetimeRangeSegmentSchema
>;

export const rcoCheckoutProbabilitySegmentSchema =
  CheckoutProbabilitySegmentSchema;
export type RcoCheckoutProbabilitySegmentSchema = z.infer<
  typeof rcoCheckoutProbabilitySegmentSchema
>;

export const rcoLineItemPropertiesSegmentSchema =
  LineItemPropertiesSegmentSchema;
export type RcoLineItemPropertiesSegmentSchema = z.infer<
  typeof rcoLineItemPropertiesSegmentSchema
>;

const rcoSegmentSchema = z.union([
  rcoDeliveryDistanceMilesSegmentSchema,
  rcoOrderCountSegmentSchema,
  rcoDeliveryLocationSegmentSchema,
  rcoCustomerTagsSegmentSchema,
  rcoProductTagsSegmentSchema,
  rcoProductCollectionsSegmentSchema,
  rcoProductVendorSegmentSchema,
  rcoProductSkuSegmentSchema,
  rcoItemQuantitySegmentSchema,
  rcoOriginLocationSegmentSchema,
  rcoCustomerReturnRateSegmentSchema,
  rcoTimeOfDaySegmentSchema,
  rcoDatetimeRangeSegmentSchema,
  rcoCheckoutProbabilitySegmentSchema,
  rcoLineItemPropertiesSegmentSchema,
]);
export type RcoSegmentSchema = z.infer<typeof rcoSegmentSchema>;

export const rcoSegmentStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.RCO_SEGMENT),
  matchingNextId: z.string(),
  nonMatchingNextId: z.string(),
  segment: rcoSegmentSchema,
});
export interface RcoSegmentStep extends z.infer<typeof rcoSegmentStepSchema> {}
/* End of segmentation */

/* Checkout parcels */
const rcoCheckoutPackageStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RCO_CHECKOUT_PACKAGE),
  parcelId: zExt.objectId(),
  name: z.string(),
  dimensions: z.string(),
});
const rcoCheckoutPackageStepSchema: z.ZodType<RcoCheckoutPackageStep> =
  rcoCheckoutPackageStepSchema_;
export interface RcoCheckoutPackageStep
  extends z.infer<typeof rcoCheckoutPackageStepSchema_> {}

const baseRcoCheckoutPackageConditionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.RCO_CHECKOUT_PACKAGE_CONDITION),
  nextTrueId: z.string(),
  nextFalseId: z.string(),
});

const rcoCheckoutPackageWeightOzConditionStepSchema_ =
  baseRcoCheckoutPackageConditionStepSchema.extend({
    condition: z.object({
      kind: z.literal(ParcelConditionKind.WEIGHT_OZ),
      matchType: createEnum(numberComparisonInequalityOperators),
      matchValue: z.number(),
    }),
  });
const rcoCheckoutPackageWeightOzConditionStepSchema: z.ZodType<RcoCheckoutPackageWeightOzConditionStep> =
  rcoCheckoutPackageWeightOzConditionStepSchema_;
export interface RcoCheckoutPackageWeightOzConditionStep
  extends z.infer<typeof rcoCheckoutPackageWeightOzConditionStepSchema_> {}

const rcoCheckoutPackageItemCountConditionStepSchema_ =
  baseRcoCheckoutPackageConditionStepSchema.extend({
    condition: z.object({
      kind: z.literal(ParcelConditionKind.ITEM_COUNT),
      matchType: createEnum(numberComparisonOperators),
      matchValue: z.number(),
    }),
  });
const rcoCheckoutPackageItemCountConditionStepSchema: z.ZodType<RcoCheckoutPackageItemCountConditionStep> =
  rcoCheckoutPackageItemCountConditionStepSchema_;
export interface RcoCheckoutPackageItemCountConditionStep
  extends z.infer<typeof rcoCheckoutPackageItemCountConditionStepSchema_> {}

const rcoCheckoutPackageProductTagConditionStepSchema_ =
  baseRcoCheckoutPackageConditionStepSchema.extend({
    condition: z.object({
      kind: z.literal(ParcelConditionKind.PRODUCT_TAGS),
      matchType: createEnum(ListToListMatchType),
      matchValues: z.array(z.string()),
      requireExactMatch: z.boolean(),
    }),
  });
const rcoCheckoutPackageProductTagConditionStepSchema: z.ZodType<RcoCheckoutPackageProductTagConditionStep> =
  rcoCheckoutPackageProductTagConditionStepSchema_;
export interface RcoCheckoutPackageProductTagConditionStep
  extends z.infer<typeof rcoCheckoutPackageProductTagConditionStepSchema_> {}

const rcoCheckoutPackageConditionStepSchema_ = z.union([
  rcoCheckoutPackageWeightOzConditionStepSchema,
  rcoCheckoutPackageItemCountConditionStepSchema,
  rcoCheckoutPackageProductTagConditionStepSchema,
]);
const rcoCheckoutPackageConditionStepSchema: z.ZodType<RcoCheckoutPackageConditionStep> =
  rcoCheckoutPackageConditionStepSchema_;
export type RcoCheckoutPackageConditionStep = z.infer<
  typeof rcoCheckoutPackageConditionStepSchema_
>;

/* End of checkout parcels */

/* End of checkout personalization */

/* Post-purchase flows */
const postPurchasePageStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.POST_PURCHASE_PAGE),
  name: z.string().nullable(),
  upsellPageId: zExt.objectId(),
  acceptNextId: z.string().optional(),
  declineNextId: z.string().optional(),
});
export const postPurchasePageStepSchema: z.ZodType<PostPurchasePageStep> =
  postPurchasePageStepSchema_;
export interface PostPurchasePageStep
  extends z.infer<typeof postPurchasePageStepSchema_> {}

/* Post-purchase segmentation */
const postPurchaseOrderValueSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentablePostPurchaseCharacteristic.ORDER_VALUE,
  ),
  amount: z.string(),
  currency: createEnum(Currency),
  operator: createEnum(PostPurchaseSegmentNumberOperator),
});
const postPurchaseOrderValueSegmentSchema: z.ZodType<PostPurchaseOrderValueSegment> =
  postPurchaseOrderValueSegmentSchema_;
export interface PostPurchaseOrderValueSegment
  extends z.infer<typeof postPurchaseOrderValueSegmentSchema_> {}

const postPurchaseProductOrderedSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentablePostPurchaseCharacteristic.PRODUCTS_ORDERED,
  ),
  productIds: z.array(z.string()),
  productListOperator: createEnum(SegmentListSingleItemMembershipOperator),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator),
});
const postPurchaseProductOrderedSegmentSchema: z.ZodType<PostPurchaseProductOrderedSegment> =
  postPurchaseProductOrderedSegmentSchema_;
export interface PostPurchaseProductOrderedSegment
  extends z.infer<typeof postPurchaseProductOrderedSegmentSchema_> {}

const postPurchaseProductCollectionSegmentSchema_ = z.object({
  segmentableCharacteristic: z.literal(
    SegmentablePostPurchaseCharacteristic.PRODUCT_COLLECTIONS,
  ),
  collectionIds: z.array(z.string()),
  collectionListOperator: createEnum(SegmentListMultipleItemMembershipOperator),
  cartOperator: createEnum(SegmentListMultipleItemMembershipOperator),
});
const postPurchaseProductCollectionSegmentSchema: z.ZodType<PostPurchaseProductCollectionSegment> =
  postPurchaseProductCollectionSegmentSchema_;
export interface PostPurchaseProductCollectionSegment
  extends z.infer<typeof postPurchaseProductCollectionSegmentSchema_> {}

const postPurchaseConditionSchema_ = z.union([
  postPurchaseOrderValueSegmentSchema,
  postPurchaseProductOrderedSegmentSchema,
  postPurchaseProductCollectionSegmentSchema,
]);
export const postPurchaseConditionSchema: z.ZodType<PostPurchaseCondition> =
  postPurchaseConditionSchema_;
export type PostPurchaseCondition = z.infer<
  typeof postPurchaseConditionSchema_
>;

export const postPurchaseConditionBlockSchema = z.object({
  conjunction: createEnum(ConjunctionMode),
  conditions: z.array(postPurchaseConditionSchema).min(1),
});
export type PostPurchaseConditionBlock = z.infer<
  typeof postPurchaseConditionBlockSchema
>;

export const postPurchaseSegmentSchema = z.object({
  conjunction: createEnum(ConjunctionMode),
  conditionBlocks: z.array(postPurchaseConditionBlockSchema).min(1),
});
export type PostPurchaseSegment = z.infer<typeof postPurchaseSegmentSchema>;

export const postPurchaseSegmentStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.POST_PURCHASE_SEGMENT),
  matchingNextId: z.string().optional(),
  nonMatchingNextId: z.string().optional(),
  segment: postPurchaseSegmentSchema,
});
export interface PostPurchaseSegmentStep
  extends z.infer<typeof postPurchaseSegmentStepSchema> {}
/* End of post-purchase segmentation */

/* End of post-purchase flows */

const setShippingServiceActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SHIPPING_SERVICE),
  actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE),
  newShippingService: ServiceSchema,
  newShippingCarrier: CarrierSchema,
});
const setShippingServiceActionStepSchema: z.ZodType<SetShippingServiceActionStep> =
  setShippingServiceActionStepSchema_;
export interface SetShippingServiceActionStep
  extends z.infer<typeof setShippingServiceActionStepSchema_> {}

const setParcelActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PARCEL),
  actionType: z.literal(OmsActionType.SET_PARCEL),
  parcels: z.array(OutboundParcelSchema).nullish(),
  /** @deprecated  use parcels instead */
  parcel: ParcelSchema.nullish(),
});
const setParcelActionStepSchema: z.ZodType<SetParcelActionStep> =
  setParcelActionStepSchema_;
export interface SetParcelActionStep
  extends z.infer<typeof setParcelActionStepSchema_> {}

const setShippingServiceCheapestActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.SHIPPING_SERVICE_CHEAPEST),
    actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST),
    servicesToChooseFrom: z.array(
      z.object({ service: ServiceSchema, carrier: createEnum(Carriers) }),
    ),
    servicesToPrioritize: z
      .array(
        z.object({ service: ServiceSchema, carrier: createEnum(Carriers) }),
      )
      .optional(),
    maxPriorityPriceDifference: z.number().optional(),
    selectionCriteria: z.literal(SortingCriteria.CHEAPEST),
  });
const setShippingServiceCheapestActionStepSchema: z.ZodType<SetShippingServiceCheapestActionStep> =
  setShippingServiceCheapestActionStepSchema_;
export interface SetShippingServiceCheapestActionStep
  extends z.infer<typeof setShippingServiceCheapestActionStepSchema_> {}

const setShippingServiceFastestActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.SHIPPING_SERVICE_FASTEST),
    actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE_FASTEST),
    servicesToChooseFrom: z.array(
      z.object({ service: ServiceSchema, carrier: createEnum(Carriers) }),
    ),
    servicesToPrioritize: z
      .array(
        z.object({ service: ServiceSchema, carrier: createEnum(Carriers) }),
      )
      .optional(),
    maxPriorityPriceDifference: z.number().optional(),
    selectionCriteria: z.literal(SortingCriteria.FASTEST),
  });
const setShippingServiceFastestActionStepSchema: z.ZodType<SetShippingServiceFastestActionStep> =
  setShippingServiceFastestActionStepSchema_;
export interface SetShippingServiceFastestActionStep
  extends z.infer<typeof setShippingServiceFastestActionStepSchema_> {}

const setShippingServiceCheapestWithinDeliveryDaysActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(
      OmsSettableFields.SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS,
    ),
    actionType: z.literal(
      OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST_WITHIN_DELIVERY_DAYS,
    ),
    servicesToChooseFrom: z.array(
      z.object({ service: ServiceSchema, carrier: createEnum(Carriers) }),
    ),
    servicesToPrioritize: z
      .array(
        z.object({ service: ServiceSchema, carrier: createEnum(Carriers) }),
      )
      .optional(),
    maxPriorityPriceDifference: z.number().optional(),
    targetDeliveryDays: z.number(),
    selectionCriteria: z.literal(SortingCriteria.CHEAPEST_WITHIN_DELIVERY_DAYS),
  });
const setShippingServiceCheapestWithinDeliveryDaysActionStepSchema: z.ZodType<SetShippingServiceCheapestWithinDeliveryDaysActionStep> =
  setShippingServiceCheapestWithinDeliveryDaysActionStepSchema_;
export interface SetShippingServiceCheapestWithinDeliveryDaysActionStep
  extends z.infer<
    typeof setShippingServiceCheapestWithinDeliveryDaysActionStepSchema_
  > {}

const setPackingSlipTemplateActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PACKING_SLIP_TEMPLATE),
  actionType: z.literal(OmsActionType.SET_PACKING_SLIP_TEMPLATE),
  packingSlipName: z.string(),
  packingSlipTemplateId: z.string(),
});
const setPackingSlipTemplateActionStepSchema: z.ZodType<SetPackingSlipTemplateActionStep> =
  setPackingSlipTemplateActionStepSchema_;
export interface SetPackingSlipTemplateActionStep
  extends z.infer<typeof setPackingSlipTemplateActionStepSchema_> {}

const setThirdPartyBillingAccountActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.THIRD_PARTY_BILLING_ACCOUNT),
    actionType: z.literal(OmsActionType.SET_THIRD_PARTY_BILLING_ACCOUNT),
    thirdPartyBillingAccountId: z.string(),
    thirdPartyBillingAccountName: z.string(),
  });
const setThirdPartyBillingAccountActionStepSchema: z.ZodType<SetThirdPartyBillingAccountActionStep> =
  setThirdPartyBillingAccountActionStepSchema_;
export interface SetThirdPartyBillingAccountActionStep
  extends z.infer<typeof setThirdPartyBillingAccountActionStepSchema_> {}

const setDeliveryMethodActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.DELIVERY_METHOD),
  actionType: z.literal(OmsActionType.SET_DELIVERY_METHOD),
  deliveryMethod: createEnum(RedoShopifyDeliveryMethodType),
});
const setDeliveryMethodActionStepSchema: z.ZodType<SetDeliveryMethodActionStep> =
  setDeliveryMethodActionStepSchema_;
export interface SetDeliveryMethodActionStep
  extends z.infer<typeof setDeliveryMethodActionStepSchema_> {}

const setOverrideUseOriginalDestinationAddressActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(
      OmsSettableFields.OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS,
    ),
    actionType: z.literal(
      OmsActionType.SET_OVERRIDE_USE_ORIGINAL_DESTINATION_ADDRESS,
    ),
    overrideUseOriginalDestinationAddress: z.boolean(),
  });
const setOverrideUseOriginalDestinationAddressActionStepSchema: z.ZodType<SetOverrideUseOriginalDestinationAddressActionStep> =
  setOverrideUseOriginalDestinationAddressActionStepSchema_;
export interface SetOverrideUseOriginalDestinationAddressActionStep
  extends z.infer<
    typeof setOverrideUseOriginalDestinationAddressActionStepSchema_
  > {}

const addTagActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.TAG),
  actionType: z.literal(OmsActionType.ADD_TAG),
  tags: z.array(TagSchema),
});
const addTagActionStepSchema: z.ZodType<AddTagActionStep> =
  addTagActionStepSchema_;
export interface AddTagActionStep
  extends z.infer<typeof addTagActionStepSchema_> {}

const placeHoldActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PLACE_HOLD),
  actionType: z.literal(OmsActionType.PLACE_HOLD),
  holdOptions: z.object({
    holdType: createEnum([AutoHoldType.Duration, AutoHoldType.Indefinite]),
    duration: z.number().optional(),
    timeUnit: createEnum(TimeUnit).optional(),
    holdReason: createEnum(ShopifyFulfillmentHoldReason),
    reasonNotes: z.string().nullish(),
  }),
});
const placeHoldActionStepSchema: z.ZodType<PlaceHoldActionStep> =
  placeHoldActionStepSchema_;
export interface PlaceHoldActionStep
  extends z.infer<typeof placeHoldActionStepSchema_> {}

const placeHoldUntilActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PLACE_HOLD_UNTIL),
  actionType: z.literal(OmsActionType.PLACE_HOLD_UNTIL),
  holdOptions: z.object({
    holdType: z.literal(AutoHoldType.Date),
    date: z.coerce.date(),
    holdReason: createEnum(ShopifyFulfillmentHoldReason),
    reasonNotes: z.string().nullish(),
  }),
});
const placeHoldUntilActionStepSchema: z.ZodType<PlaceHoldUntilActionStep> =
  placeHoldUntilActionStepSchema_;
export interface PlaceHoldUntilActionStep
  extends z.infer<typeof placeHoldUntilActionStepSchema_> {}

const setWeightActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SET_WEIGHT),
  actionType: z.literal(OmsActionType.SET_WEIGHT),
  weight: WeightSchema,
});
const setWeightActionStepSchema: z.ZodType<SetWeightActionStep> =
  setWeightActionStepSchema_;

const roundWeightActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.ROUND_WEIGHT),
  actionType: z.literal(OmsActionType.ROUND_WEIGHT),
  roundingType: createEnum(WeightRoundingType),
  minimumWeight: WeightSchema,
});
const roundWeightActionStepSchema: z.ZodType<RoundWeightActionStep> =
  roundWeightActionStepSchema_;

const setAssignedUserActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.ASSIGNED_USER),
  actionType: z.literal(OmsActionType.SET_ASSIGNED_USER),
  userId: z.string(),
});
const setAssignedUserActionStepSchema: z.ZodType<SetAssignedUserActionStep> =
  setAssignedUserActionStepSchema_;
export interface SetAssignedUserActionStep
  extends z.infer<typeof setAssignedUserActionStepSchema_> {}

const setDutiesPayerActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.DUTIES_PAYER),
  actionType: z.literal(OmsActionType.SET_DUTIES_PAYER),
  ddp: z.boolean(),
});
const setDutiesPayerActionStepSchema: z.ZodType<SetDutiesPayerActionStep> =
  setDutiesPayerActionStepSchema_;
export interface SetDutiesPayerActionStep
  extends z.infer<typeof setDutiesPayerActionStepSchema_> {}

const setTaxIdentifiersActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.TAX_IDENTIFIERS),
  actionType: z.literal(OmsActionType.SET_TAX_IDENTIFIERS),
  taxIdentifierIds: z.array(z.string()),
});
const setTaxIdentifiersActionStepSchema: z.ZodType<SetTaxIdentifiersActionStep> =
  setTaxIdentifiersActionStepSchema_;
export interface SetTaxIdentifiersActionStep
  extends z.infer<typeof setTaxIdentifiersActionStepSchema_> {}

const setReturnAddressActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.RETURN_ADDRESS),
  actionType: z.literal(OmsActionType.SET_RETURN_ADDRESS),
  address: FulfillmentOrderAddressWithIdSchema,
});
const setReturnAddressActionStepSchema: z.ZodType<SetReturnAddressActionStep> =
  setReturnAddressActionStepSchema_;
export interface SetReturnAddressActionStep
  extends z.infer<typeof setReturnAddressActionStepSchema_> {}

const setOriginLocationActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.ORIGIN_LOCATION),
  actionType: z.literal(OmsActionType.SET_ORIGIN_LOCATION),
  locationId: z.string(),
});
const setOriginLocationActionStepSchema: z.ZodType<SetOriginLocationActionStep> =
  setOriginLocationActionStepSchema_;
export interface SetOriginLocationActionStep
  extends z.infer<typeof setOriginLocationActionStepSchema_> {}

const setBuyReturnLabelActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.BUY_RETURN_LABEL),
  actionType: z.literal(OmsActionType.SET_BUY_RETURN_LABEL),
  buyReturnLabel: z.boolean(),
});
const setBuyReturnLabelActionStepSchema: z.ZodType<SetBuyReturnLabelActionStep> =
  setBuyReturnLabelActionStepSchema_;
export interface SetBuyReturnLabelActionStep
  extends z.infer<typeof setBuyReturnLabelActionStepSchema_> {}

const assignFulfillmentServiceActionStepSchema_ =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.FULFILLMENT_SERVICE),
    actionType: z.literal(OmsActionType.ASSIGN_FULFILLMENT_SERVICE),
    fulfillmentServiceId: z.string(),
    fulfillmentServiceName: z.string(),
  });
const assignFulfillmentServiceActionStepSchema: z.ZodType<AssignFulfillmentServiceActionStep> =
  assignFulfillmentServiceActionStepSchema_;
export interface AssignFulfillmentServiceActionStep
  extends z.infer<typeof assignFulfillmentServiceActionStepSchema_> {}

const shippingOptionsSchema_ = z.object({
  signatureRequired: z.boolean().nullish(),
  adultSignatureRequired: z.boolean().nullish(),
  includeInsurance: z.boolean().nullish(),
  specialRateEligibility: createEnum(SpecialRateEligibility).nullish(),
  hazmat: createEnum(Hazmat).nullish(),
  dryIceWeight: WeightSchema.nullish(),
});
const shippingOptionsSchema: z.ZodType<ShippingOptions> =
  shippingOptionsSchema_;
export interface ShippingOptions
  extends z.infer<typeof shippingOptionsSchema_> {}

const setShippingOptionsActionStepSchema_ = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SHIPPING_OPTIONS),
  actionType: z.literal(OmsActionType.SET_SHIPPING_OPTIONS),
  shippingOptions: shippingOptionsSchema,
});
const setShippingOptionsActionStepSchema: z.ZodType<SetShippingOptionsActionStep> =
  setShippingOptionsActionStepSchema_;
export interface SetShippingOptionsActionStep
  extends z.infer<typeof setShippingOptionsActionStepSchema_> {}

const baseOmsConditionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.OMS_CONDITION),
  conditionType: createEnum(OmsConditionType),
  nextTrueId: z.string(),
  nextFalseId: z.string(),
});

const baseOmsConditionStepSchemaWithWhere = baseOmsConditionStepSchema.extend({
  where: z
    .array(
      baseOmsConditionStepSchema.omit({
        id: true,
        type: true,
        nextTrueId: true,
        nextFalseId: true,
      }),
    )
    .optional(),
});

const omsOrderTagConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ORDER_TAG),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });
const omsOrderTagConditionStepSchema: z.ZodType<OmsOrderTagConditionStep> =
  omsOrderTagConditionStepSchema_;
export interface OmsOrderTagConditionStep
  extends z.infer<typeof omsOrderTagConditionStepSchema_> {}

const omsSelectedShippingConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SELECTED_SHIPPING),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });
const omsSelectedShippingConditionStepSchema: z.ZodType<OmsSelectedShippingConditionStep> =
  omsSelectedShippingConditionStepSchema_;
export interface OmsSelectedShippingConditionStep
  extends z.infer<typeof omsSelectedShippingConditionStepSchema_> {}

const baseOmsSkuConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SKU),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsVendorConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.VENDOR),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsProductNameConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_NAME),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsVariantTitleConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.VARIANT_TITLE),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsProductTypeConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_TYPE),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const baseOmsItemQuantityConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ITEM_QUANTITY),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });

const omsPackageWeightOzConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PACKAGE_WEIGHT_OZ),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsPackageWeightOzConditionStepSchema: z.ZodType<OmsPackageWeightOzConditionStep> =
  omsPackageWeightOzConditionStepSchema_;
export interface OmsPackageWeightOzConditionStep
  extends z.infer<typeof omsPackageWeightOzConditionStepSchema_> {}

const omsTotalValueConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.TOTAL_VALUE),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsTotalValueConditionStepSchema: z.ZodType<OmsTotalValueConditionStep> =
  omsTotalValueConditionStepSchema_;
export interface OmsTotalValueConditionStep
  extends z.infer<typeof omsTotalValueConditionStepSchema_> {}

const omsTaxesPaidConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.TAXES_PAID),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsTaxesPaidConditionStepSchema: z.ZodType<OmsTaxesPaidConditionStep> =
  omsTaxesPaidConditionStepSchema_;
export interface OmsTaxesPaidConditionStep
  extends z.infer<typeof omsTaxesPaidConditionStepSchema_> {}

const omsDestinationCountryConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.DESTINATION_COUNTRY),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsDestinationCountryConditionStepSchema: z.ZodType<OmsDestinationCountryConditionStep> =
  omsDestinationCountryConditionStepSchema_;
export interface OmsDestinationCountryConditionStep
  extends z.infer<typeof omsDestinationCountryConditionStepSchema_> {}

const omsDestinationProvinceConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.DESTINATION_PROVINCE),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsDestinationProvinceConditionStepSchema: z.ZodType<OmsDestinationProvinceConditionStep> =
  omsDestinationProvinceConditionStepSchema_;
export interface OmsDestinationProvinceConditionStep
  extends z.infer<typeof omsDestinationProvinceConditionStepSchema_> {}

const omsDeliveryMethodConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.DELIVERY_METHOD),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsDeliveryMethodConditionStepSchema: z.ZodType<OmsDeliveryMethodConditionStep> =
  omsDeliveryMethodConditionStepSchema_;
export interface OmsDeliveryMethodConditionStep
  extends z.infer<typeof omsDeliveryMethodConditionStepSchema_> {}

const omsStreetAddressConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.STREET_ADDRESS),
    matchType: createEnum(textMatchOperators).optional(),
    matchValue: z.string(),
  });
const omsStreetAddressConditionStepSchema: z.ZodType<OmsStreetAddressConditionStep> =
  omsStreetAddressConditionStepSchema_;
export interface OmsStreetAddressConditionStep
  extends z.infer<typeof omsStreetAddressConditionStepSchema_> {}

const omsStreetAddressListInputConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.STREET_ADDRESS_LIST_INPUT),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsStreetAddressListInputConditionStepSchema: z.ZodType<OmsStreetAddressListInputConditionStep> =
  omsStreetAddressListInputConditionStepSchema_;
export interface OmsStreetAddressListInputConditionStep
  extends z.infer<typeof omsStreetAddressListInputConditionStepSchema_> {}

const omsSalesChannelConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SALES_CHANNEL),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsSalesChannelConditionStepSchema: z.ZodType<OmsSalesChannelConditionStep> =
  omsSalesChannelConditionStepSchema_;
export interface OmsSalesChannelConditionStep
  extends z.infer<typeof omsSalesChannelConditionStepSchema_> {}

const baseOmsProductTagConditionStepSchema =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.PRODUCT_TAG),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const omsProductWhereClauseSchema = z.union([
  baseOmsProductNameConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsVariantTitleConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsProductTypeConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsProductTagConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsItemQuantityConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsSkuConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
  baseOmsVendorConditionStepSchema.omit({
    id: true,
    type: true,
    nextTrueId: true,
    nextFalseId: true,
  }),
]);

const omsProductNameConditionStepSchema_ =
  baseOmsProductNameConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsProductNameConditionStepSchema: z.ZodType<OmsProductNameConditionStep> =
  omsProductNameConditionStepSchema_;
export interface OmsProductNameConditionStep
  extends z.infer<typeof omsProductNameConditionStepSchema_> {}

const omsVariantTitleConditionStepSchema_ =
  baseOmsVariantTitleConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsVariantTitleConditionStepSchema: z.ZodType<OmsVariantTitleConditionStep> =
  omsVariantTitleConditionStepSchema_;
export interface OmsVariantTitleConditionStep
  extends z.infer<typeof omsVariantTitleConditionStepSchema_> {}

const omsSkuConditionStepSchema_ = baseOmsSkuConditionStepSchema.extend({
  where: z.array(omsProductWhereClauseSchema).optional(),
});
const omsSkuConditionStepSchema: z.ZodType<OmsSkuConditionStep> =
  omsSkuConditionStepSchema_;
export interface OmsSkuConditionStep
  extends z.infer<typeof omsSkuConditionStepSchema_> {}

const omsVendorConditionStepSchema_ = baseOmsVendorConditionStepSchema.extend({
  where: z.array(omsProductWhereClauseSchema).optional(),
});
const omsVendorConditionStepSchema: z.ZodType<OmsVendorConditionStep> =
  omsVendorConditionStepSchema_;
export interface OmsVendorConditionStep
  extends z.infer<typeof omsVendorConditionStepSchema_> {}

const omsProductTypeConditionStepSchema_ =
  baseOmsProductTypeConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsProductTypeConditionStepSchema: z.ZodType<OmsProductTypeConditionStep> =
  omsProductTypeConditionStepSchema_;
export interface OmsProductTypeConditionStep
  extends z.infer<typeof omsProductTypeConditionStepSchema_> {}

const omsUspsZoneConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.USPS_ZONE),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsUspsZoneConditionStepSchema: z.ZodType<OmsUspsZoneConditionStep> =
  omsUspsZoneConditionStepSchema_;
export interface OmsUspsZoneConditionStep
  extends z.infer<typeof omsUspsZoneConditionStepSchema_> {}

const omsProductTagConditionStepSchema_ =
  baseOmsProductTagConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsProductTagConditionStepSchema: z.ZodType<OmsProductTagConditionStep> =
  omsProductTagConditionStepSchema_;
export interface OmsProductTagConditionStep
  extends z.infer<typeof omsProductTagConditionStepSchema_> {}

const omsItemQuantityConditionStepSchema_ =
  baseOmsItemQuantityConditionStepSchema.extend({
    where: z.array(omsProductWhereClauseSchema).optional(),
  });
const omsItemQuantityConditionStepSchema: z.ZodType<OmsItemQuantityConditionStep> =
  omsItemQuantityConditionStepSchema_;
export interface OmsItemQuantityConditionStep
  extends z.infer<typeof omsItemQuantityConditionStepSchema_> {}

const omsFulfillmentLocationConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.FULFILLMENT_LOCATION),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsFulfillmentLocationConditionStepSchema: z.ZodType<OmsFulfillmentLocationConditionStep> =
  omsFulfillmentLocationConditionStepSchema_;
export interface OmsFulfillmentLocationConditionStep
  extends z.infer<typeof omsFulfillmentLocationConditionStepSchema_> {}

const omsOriginStoreConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ORIGIN_STORE),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsOriginStoreConditionStepSchema: z.ZodType<OmsOriginStoreConditionStep> =
  omsOriginStoreConditionStepSchema_;
export interface OmsOriginStoreConditionStep
  extends z.infer<typeof omsOriginStoreConditionStepSchema_> {}

const omsOrderNotesConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.ORDER_NOTES),
    matchType: createEnum(textMatchOperators).optional(),
    matchValue: z.string(),
  });
const omsOrderNotesConditionStepSchema: z.ZodType<OmsOrderNotesConditionStep> =
  omsOrderNotesConditionStepSchema_;
export interface OmsOrderNotesConditionStep
  extends z.infer<typeof omsOrderNotesConditionStepSchema_> {}

export const omsActionStepSchema = z.union([
  setShippingServiceActionStepSchema,
  setParcelActionStepSchema,
  setShippingServiceCheapestActionStepSchema,
  setShippingServiceFastestActionStepSchema,
  setShippingServiceCheapestWithinDeliveryDaysActionStepSchema,
  setPackingSlipTemplateActionStepSchema,
  setThirdPartyBillingAccountActionStepSchema,
  addTagActionStepSchema,
  setShippingOptionsActionStepSchema,
  placeHoldActionStepSchema,
  placeHoldUntilActionStepSchema,
  setWeightActionStepSchema,
  roundWeightActionStepSchema,
  setAssignedUserActionStepSchema,
  setDutiesPayerActionStepSchema,
  setTaxIdentifiersActionStepSchema,
  setReturnAddressActionStepSchema,
  setOriginLocationActionStepSchema,
  setBuyReturnLabelActionStepSchema,
  assignFulfillmentServiceActionStepSchema,
  setDeliveryMethodActionStepSchema,
  setOverrideUseOriginalDestinationAddressActionStepSchema,
]);
export type OmsActionStep = z.infer<typeof omsActionStepSchema>;

const omsFraudRiskConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.FRAUD_RISK),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsFraudRiskConditionStepSchema: z.ZodType<OmsFraudRiskConditionStep> =
  omsFraudRiskConditionStepSchema_;
export interface OmsFraudRiskConditionStep
  extends z.infer<typeof omsFraudRiskConditionStepSchema_> {}

const omsAvailableInventoryConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.AVAILABLE_INVENTORY),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsAvailableInventoryConditionStepSchema: z.ZodType<OmsAvailableInventoryConditionStep> =
  omsAvailableInventoryConditionStepSchema_;
export interface OmsAvailableInventoryConditionStep
  extends z.infer<typeof omsAvailableInventoryConditionStepSchema_> {}

const omsMarketplaceConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.MARKETPLACE),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsMarketplaceConditionStepSchema: z.ZodType<OmsMarketplaceConditionStep> =
  omsMarketplaceConditionStepSchema_;
export interface OmsMarketplaceConditionStep
  extends z.infer<typeof omsMarketplaceConditionStepSchema_> {}

const omsShipmentTagConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.SHIPMENT_TAG),
    matchType: createEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const omsShipmentTagConditionStepSchema: z.ZodType<OmsShipmentTagConditionStep> =
  omsShipmentTagConditionStepSchema_;

export interface OmsShipmentTagConditionStep
  extends z.infer<typeof omsShipmentTagConditionStepSchema_> {}

const omsCustomString1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_STRING_1),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsCustomString1ConditionStepSchema: z.ZodType<OmsCustomString1ConditionStep> =
  omsCustomString1ConditionStepSchema_;
export interface OmsCustomString1ConditionStep
  extends z.infer<typeof omsCustomString1ConditionStepSchema_> {}

const omsCustomString2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_STRING_2),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsCustomString2ConditionStepSchema: z.ZodType<OmsCustomString2ConditionStep> =
  omsCustomString2ConditionStepSchema_;
export interface OmsCustomString2ConditionStep
  extends z.infer<typeof omsCustomString2ConditionStepSchema_> {}

const omsCustomString3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_STRING_3),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsCustomString3ConditionStepSchema: z.ZodType<OmsCustomString3ConditionStep> =
  omsCustomString3ConditionStepSchema_;
export interface OmsCustomString3ConditionStep
  extends z.infer<typeof omsCustomString3ConditionStepSchema_> {}

const omsCustomNumber1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_NUMBER_1),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsCustomNumber1ConditionStepSchema: z.ZodType<OmsCustomNumber1ConditionStep> =
  omsCustomNumber1ConditionStepSchema_;
export interface OmsCustomNumber1ConditionStep
  extends z.infer<typeof omsCustomNumber1ConditionStepSchema_> {}

const omsCustomNumber2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_NUMBER_2),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsCustomNumber2ConditionStepSchema: z.ZodType<OmsCustomNumber2ConditionStep> =
  omsCustomNumber2ConditionStepSchema_;
export interface OmsCustomNumber2ConditionStep
  extends z.infer<typeof omsCustomNumber2ConditionStepSchema_> {}

const omsCustomNumber3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_NUMBER_3),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsCustomNumber3ConditionStepSchema: z.ZodType<OmsCustomNumber3ConditionStep> =
  omsCustomNumber3ConditionStepSchema_;
export interface OmsCustomNumber3ConditionStep
  extends z.infer<typeof omsCustomNumber3ConditionStepSchema_> {}

const omsCustomDate1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_DATE_1),
    matchType: createEnum(dateComparisonOperators).optional(),
    matchDate: z.date(),
  });
const omsCustomDate1ConditionStepSchema: z.ZodType<OmsCustomDate1ConditionStep> =
  omsCustomDate1ConditionStepSchema_;
export interface OmsCustomDate1ConditionStep
  extends z.infer<typeof omsCustomDate1ConditionStepSchema_> {}

const omsCustomDate2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_DATE_2),
    matchType: createEnum(dateComparisonOperators).optional(),
    matchDate: z.date(),
  });
const omsCustomDate2ConditionStepSchema: z.ZodType<OmsCustomDate2ConditionStep> =
  omsCustomDate2ConditionStepSchema_;
export interface OmsCustomDate2ConditionStep
  extends z.infer<typeof omsCustomDate2ConditionStepSchema_> {}

const omsCustomDate3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_DATE_3),
    matchType: createEnum(dateComparisonOperators).optional(),
    matchDate: z.date(),
  });
const omsCustomDate3ConditionStepSchema: z.ZodType<OmsCustomDate3ConditionStep> =
  omsCustomDate3ConditionStepSchema_;
export interface OmsCustomDate3ConditionStep
  extends z.infer<typeof omsCustomDate3ConditionStepSchema_> {}

const omsCustomAttributeString1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsCustomAttributeString1ConditionStepSchema: z.ZodType<OmsCustomAttributeString1ConditionStep> =
  omsCustomAttributeString1ConditionStepSchema_;
export interface OmsCustomAttributeString1ConditionStep
  extends z.infer<typeof omsCustomAttributeString1ConditionStepSchema_> {}

const omsCustomAttributeString2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsCustomAttributeString2ConditionStepSchema: z.ZodType<OmsCustomAttributeString2ConditionStep> =
  omsCustomAttributeString2ConditionStepSchema_;
export interface OmsCustomAttributeString2ConditionStep
  extends z.infer<typeof omsCustomAttributeString2ConditionStepSchema_> {}

const omsCustomAttributeString3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsCustomAttributeString3ConditionStepSchema: z.ZodType<OmsCustomAttributeString3ConditionStep> =
  omsCustomAttributeString3ConditionStepSchema_;
export interface OmsCustomAttributeString3ConditionStep
  extends z.infer<typeof omsCustomAttributeString3ConditionStepSchema_> {}

const omsCustomAttributeNumber1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsCustomAttributeNumber1ConditionStepSchema: z.ZodType<OmsCustomAttributeNumber1ConditionStep> =
  omsCustomAttributeNumber1ConditionStepSchema_;
export interface OmsCustomAttributeNumber1ConditionStep
  extends z.infer<typeof omsCustomAttributeNumber1ConditionStepSchema_> {}

const omsCustomAttributeNumber2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsCustomAttributeNumber2ConditionStepSchema: z.ZodType<OmsCustomAttributeNumber2ConditionStep> =
  omsCustomAttributeNumber2ConditionStepSchema_;
export interface OmsCustomAttributeNumber2ConditionStep
  extends z.infer<typeof omsCustomAttributeNumber2ConditionStepSchema_> {}

const omsCustomAttributeNumber3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsCustomAttributeNumber3ConditionStepSchema: z.ZodType<OmsCustomAttributeNumber3ConditionStep> =
  omsCustomAttributeNumber3ConditionStepSchema_;
export interface OmsCustomAttributeNumber3ConditionStep
  extends z.infer<typeof omsCustomAttributeNumber3ConditionStepSchema_> {}

const omsCustomAttributeDate1ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1),
    matchType: createEnum(dateComparisonOperators).optional(),
    matchDate: z.date(),
  });
const omsCustomAttributeDate1ConditionStepSchema: z.ZodType<OmsCustomAttributeDate1ConditionStep> =
  omsCustomAttributeDate1ConditionStepSchema_;
export interface OmsCustomAttributeDate1ConditionStep
  extends z.infer<typeof omsCustomAttributeDate1ConditionStepSchema_> {}

const omsCustomAttributeDate2ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2),
    matchType: createEnum(dateComparisonOperators).optional(),
    matchDate: z.date(),
  });
const omsCustomAttributeDate2ConditionStepSchema: z.ZodType<OmsCustomAttributeDate2ConditionStep> =
  omsCustomAttributeDate2ConditionStepSchema_;
export interface OmsCustomAttributeDate2ConditionStep
  extends z.infer<typeof omsCustomAttributeDate2ConditionStepSchema_> {}

const omsCustomAttributeDate3ConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3),
    matchType: createEnum(dateComparisonOperators).optional(),
    matchDate: z.date(),
  });
const omsCustomAttributeDate3ConditionStepSchema: z.ZodType<OmsCustomAttributeDate3ConditionStep> =
  omsCustomAttributeDate3ConditionStepSchema_;
export interface OmsCustomAttributeDate3ConditionStep
  extends z.infer<typeof omsCustomAttributeDate3ConditionStepSchema_> {}

const omsCustomerNameConditionStepSchema_ =
  baseOmsConditionStepSchemaWithWhere.extend({
    conditionType: z.literal(OmsConditionType.CUSTOMER_NAME),
    matchType: createEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });
const omsCustomerNameConditionStepSchema: z.ZodType<OmsCustomerNameConditionStep> =
  omsCustomerNameConditionStepSchema_;
export interface OmsCustomerNameConditionStep
  extends z.infer<typeof omsCustomerNameConditionStepSchema_> {}

const omsCustomerOrderCountConditionStepSchema_ =
  baseOmsConditionStepSchema.extend({
    conditionType: z.literal(OmsConditionType.CUSTOMER_ORDER_COUNT),
    matchType: createEnum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  });
const omsCustomerOrderCountConditionStepSchema: z.ZodType<OmsCustomerOrderCountConditionStep> =
  omsCustomerOrderCountConditionStepSchema_;
export interface OmsCustomerOrderCountConditionStep
  extends z.infer<typeof omsCustomerOrderCountConditionStepSchema_> {}

const omsItemAmountConditionStepSchema_ = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.ITEM_COST),
  matchType: createEnum(numberComparisonOperators).optional(),
  matchValue: z.number(),
});
const omsItemAmountConditionStepSchema: z.ZodType<OmsItemAmountConditionStep> =
  omsItemAmountConditionStepSchema_;
export interface OmsItemAmountConditionStep
  extends z.infer<typeof omsItemAmountConditionStepSchema_> {}

export const omsConditionStepSchema = z.union([
  omsOrderTagConditionStepSchema,
  omsSelectedShippingConditionStepSchema,
  omsSkuConditionStepSchema,
  omsProductNameConditionStepSchema,
  omsVariantTitleConditionStepSchema,
  omsItemQuantityConditionStepSchema,
  omsPackageWeightOzConditionStepSchema,
  omsTotalValueConditionStepSchema,
  omsTaxesPaidConditionStepSchema,
  omsDestinationCountryConditionStepSchema,
  omsDestinationProvinceConditionStepSchema,
  omsDeliveryMethodConditionStepSchema,
  omsStreetAddressConditionStepSchema,
  omsStreetAddressListInputConditionStepSchema,
  omsSalesChannelConditionStepSchema,
  omsProductTagConditionStepSchema,
  omsFulfillmentLocationConditionStepSchema,
  omsOriginStoreConditionStepSchema,
  omsOrderNotesConditionStepSchema,
  omsFraudRiskConditionStepSchema,
  omsAvailableInventoryConditionStepSchema,
  omsMarketplaceConditionStepSchema,
  omsShipmentTagConditionStepSchema,
  omsCustomString1ConditionStepSchema,
  omsCustomString2ConditionStepSchema,
  omsCustomString3ConditionStepSchema,
  omsCustomNumber1ConditionStepSchema,
  omsCustomNumber2ConditionStepSchema,
  omsCustomNumber3ConditionStepSchema,
  omsCustomDate1ConditionStepSchema,
  omsCustomDate2ConditionStepSchema,
  omsCustomDate3ConditionStepSchema,
  omsCustomAttributeString1ConditionStepSchema,
  omsCustomAttributeString2ConditionStepSchema,
  omsCustomAttributeString3ConditionStepSchema,
  omsCustomAttributeNumber1ConditionStepSchema,
  omsCustomAttributeNumber2ConditionStepSchema,
  omsCustomAttributeNumber3ConditionStepSchema,
  omsCustomAttributeDate1ConditionStepSchema,
  omsCustomAttributeDate2ConditionStepSchema,
  omsCustomAttributeDate3ConditionStepSchema,
  omsCustomerNameConditionStepSchema,
  omsCustomerOrderCountConditionStepSchema,
  omsItemAmountConditionStepSchema,
  omsUspsZoneConditionStepSchema,
  omsVendorConditionStepSchema,
  omsProductTypeConditionStepSchema,
]);
export type OmsConditionStep = z.infer<typeof omsConditionStepSchema>;

// TODO: Once zod 4 is released, switch back to discriminated union
export const upsellFlowStepSchema = z.union([
  upsellConditionStepSchema,
  upsellStepSchema,
  downsellStepSchema,
  doNothingStepSchema,
  triggerStepSchema,
]);
export type UpsellFlowStep = z.infer<typeof upsellFlowStepSchema>;

export const omsStepSchema = z.union([
  omsConditionStepSchema,
  omsActionStepSchema,
  doNothingStepSchema,
]);
export type OmsStep = z.infer<typeof omsStepSchema>;

// A/B Test step schema
const abTestStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.AB_TEST),
  name: z.string().optional(),
  description: z.string().optional(),
  variants: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      weight: z.number().min(0).max(100),
      nextId: z.string(),
    }),
  ),
});
const abTestStepSchema: z.ZodType<AbTestStep> = abTestStepSchema_;
export interface AbTestStep extends z.infer<typeof abTestStepSchema_> {}

// TODO: Once zod 4 is released, switch back to discriminated union
export const marketingStepSchema = z.union([
  triggerStepSchema,
  waitStepSchema,
  waitForEventStepSchema,
  sendEmailStepSchema,
  sendSmsStepSchema,
  sendWebhookStepSchema,
  manageCustomerTagsStepSchema,
  conditionStepSchema,
  doNothingStepSchema,
  abTestStepSchema,
  recoverStepSchema,
]);
export type MarketingStep = z.infer<typeof marketingStepSchema>;

export const checkoutOptimizationStepSchema = z.union([
  rcoSplitStepSchema,
  checkoutExperienceStepSchema,
  rcoSegmentStepSchema,
  blockCheckoutStepSchema,
  checkoutOptimizationTriggerStepSchema,
]);
export type CheckoutOptimizationStep = z.infer<
  typeof checkoutOptimizationStepSchema
>;

export const checkoutOptimizationStepArraySchema = z.array(
  checkoutOptimizationStepSchema,
);

export const checkoutParcelStepSchema = z.union([
  rcoCheckoutPackageStepSchema,
  rcoCheckoutPackageConditionStepSchema,
]);
export type CheckoutParcelStep = z.infer<typeof checkoutParcelStepSchema>;

const checkoutParcelFlowStepSchema = z.union([
  checkoutParcelStepSchema,
  checkoutPackageTriggerStepSchema,
]);
export type CheckoutParcelFlowStep = z.infer<
  typeof checkoutParcelFlowStepSchema
>;

export const checkoutParcelFlowStepArraySchema = z.array(
  checkoutParcelFlowStepSchema,
);
export type CheckoutParcelFlowStepArray = z.infer<
  typeof checkoutParcelFlowStepArraySchema
>;

export const postPurchaseFlowStepSchema = z.union([
  postPurchasePageStepSchema,
  postPurchaseSegmentStepSchema,
  doNothingStepSchema,
  postPurchaseTriggerStepSchema,
]);
export type PostPurchaseFlowStep = z.infer<typeof postPurchaseFlowStepSchema>;

export const postPurchaseFlowStepArraySchema = z.array(
  postPurchaseFlowStepSchema,
);
export type PostPurchaseFlowStepArray = z.infer<
  typeof postPurchaseFlowStepArraySchema
>;

const trackingPageStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.TRACKING_PAGE),
  trackingPageId: z.string(),
  nextId: z.string().optional(),
});
const trackingPageStepSchema: z.ZodType<TrackingPageStep> =
  trackingPageStepSchema_;
export interface TrackingPageStep
  extends z.infer<typeof trackingPageStepSchema_> {}

export const trackingPageFlowStepSchema = z.union([
  trackingPageTriggerStepSchema,
  trackingPageStepSchema,
  conditionStepSchema,
  doNothingStepSchema,
  abTestStepSchema,
]);
export type TrackingPageFlowStep = z.infer<typeof trackingPageFlowStepSchema>;

export const trackingPageFlowStepArraySchema = z.array(
  trackingPageFlowStepSchema,
);
export type TrackingPageFlowStepArray = z.infer<
  typeof trackingPageFlowStepArraySchema
>;

export const rulesFlowStepSchema = z.union([
  ruleConditionsStepSchema,
  ruleActionsStepSchema,
  waitStepSchema,
]);
export type RulesFlowStep = z.infer<typeof rulesFlowStepSchema>;

export const rulesFlowStepArraySchema = z.array(rulesFlowStepSchema);
export type RulesFlowStepArray = z.infer<typeof rulesFlowStepArraySchema>;

const stepSchema_ = z.union([
  omsStepSchema,
  marketingStepSchema,
  upsellFlowStepSchema,
  checkoutOptimizationStepSchema,
  checkoutParcelStepSchema,
  postPurchaseFlowStepSchema,
  abTestStepSchema,
  trackingPageFlowStepSchema,
  rulesFlowStepSchema,
]);
export const stepSchema: z.ZodType<FlowStep> = stepSchema_;
export type FlowStep = {} & z.infer<typeof stepSchema_>;

export const advancedFlowSchema_ = z.object({
  team: zExt.objectId(),
  name: z.string(),
  description: z.string().optional(),
  enabled: z.boolean(),
  steps: z
    .array(stepSchema)
    .refine(
      (steps) => steps.filter((s) => s.type === StepType.TRIGGER).length === 1,
      "Flow must have exactly one trigger step",
    ),
  schemaType: createEnum(SchemaType),
  category: createEnum(categories),
  createdByUserId: zExt.objectId().optional(),
  createdAt: z.date().optional(),
  index: z.number().optional(),
  versionGroupId: zExt.objectId(),
  publishedAt: z.date().optional(),
  _id: zExt.objectId(),
  updatedAt: z.date().optional(),
  __v: z.number().optional(),
});
// Backwards compatibility: normalize deprecated "sms_marketing_confirmed" to "sms_marketing_signup"
export const advancedFlowSchema: z.ZodType<AdvancedFlow> = z.preprocess(
  (val: any) => {
    if (!val || typeof val !== "object") return val;

    // Convert Mongoose document to plain object if needed
    const plainObj = val.toObject ? val.toObject() : val;

    // Normalize flow-level schemaType
    if (plainObj.schemaType === "sms_marketing_confirmed") {
      plainObj.schemaType = "sms_marketing_signup";
    }

    // Normalize trigger step schemaType
    if (Array.isArray(plainObj.steps)) {
      plainObj.steps = plainObj.steps.map((step: any) => {
        if (
          step &&
          step.type === "trigger" &&
          step.schemaType === "sms_marketing_confirmed"
        ) {
          return { ...step, schemaType: "sms_marketing_signup" };
        }
        return step;
      });
    }

    return plainObj;
  },
  advancedFlowSchema_,
) as z.ZodType<AdvancedFlow>;
// TODO: Switch to cleaner version in mid-December 2025 after migration is complete
// Replace above with:
// export const advancedFlowSchema: z.ZodType<AdvancedFlow> = advancedFlowSchema_;
export interface AdvancedFlow extends z.infer<typeof advancedFlowSchema_> {}

export type NewAdvancedFlow = Omit<
  AdvancedFlow,
  "createdAt" | "updatedAt" | "schemaVersion" | "_id" | "versionGroupId"
>;

// For every trigger key, there should be a trigger step schema
// in this file. This function expression enforces that.
// eslint-disable-next-line no-unused-expressions
() => {
  const a = "" as unknown as XorOfUnions<TriggerStep["key"], TriggerKey>;
  a satisfies never;
};
